import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { handleApiError } from './axiosConfig';
import { Camp, CampDate } from '../utils/types';
import axios from 'axios';

// Participant info as returned from API
export interface BookingParticipant {
  name: string;
  birthdate?: string;
  details?: string;
}

// Contact info as returned from API
export interface BookingContact {
  name: string;
  email: string;
  phone?: string;
  relationship_to_participant?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

export interface CreateBookingRequest {
  camp_id: string;
  camp_date_ids: string[];
}

export interface BookingDetails {
  uuid: string;
  camp: Camp;
  camp_dates: CampDate[];
  participants?: BookingParticipant[];
  contact?: BookingContact;
  client_secret?: string;
  total_price: number;
  original_price?: number;
  discount_code?: string;
  discount_amount?: number;
  created_at: string;
  status?: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'expired';
}

export interface UpdateBookingRequest {
  user_uuid?: string;
  camp_date_ids?: number[];
  contact?: {
    name: string;
    email: string;
    phone?: string;
    relationship_to_participant?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
  participants?: {
    id?: number;
    name: string;
    birthdate?: string;
    details?: string;
  }[];
}

export const useCreateBooking = () => {
  return useMutation({
    mutationFn: async (data: CreateBookingRequest): Promise<BookingDetails> => {
      try {
        return await apiClient.post<BookingDetails>('/api/bookings', data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create booking:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Booking creation failed:', error);
    },
  });
};

export const useGetBooking = (bookingUuid: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryFn: async (): Promise<BookingDetails> => {
      try {
        return await apiClient.get<BookingDetails>(`/api/bookings/${bookingUuid}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch booking:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    queryKey: ['booking', bookingUuid],
    enabled: options?.enabled !== false && Boolean(bookingUuid),
  });
};



export const useUpdateBooking = (bookingUuid: string) => {
  return useMutation({
    mutationFn: async (data: UpdateBookingRequest): Promise<BookingDetails> => {
      try {
        return await apiClient.put<BookingDetails>(`/api/bookings/${bookingUuid}`, data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update booking:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Booking update failed:', error);
    },
  });
};

export interface CompleteBookingRequest {
  contact: {
    name: string;
    email: string;
    phone?: string;
    relationship_to_participant?: string;
  };
  participants: {
    name: string;
    birthdate?: string;
    details?: string;
  }[];
}

export const useCompleteBooking = (bookingUuid: string) => {
  return useMutation({
    mutationFn: async (data: CompleteBookingRequest): Promise<BookingDetails> => {
      try {
        return await apiClient.post<BookingDetails>(`/api/bookings/${bookingUuid}/complete`, data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to complete booking:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onError: (error) => {
      console.error('Booking completion failed:', error);
    },
  });
};

export const useApplyDiscount = (bookingUuid: string) => {
  return useMutation({
    mutationFn: async (discountCode: string): Promise<BookingDetails> => {
      try {
        return await apiClient.post<BookingDetails>(`/api/bookings/${bookingUuid}/discount`, { discount_code: discountCode });
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 400) {
          throw new Error(error.response.data?.message || 'Invalid discount code');
        }
        const errorMessage = handleApiError(error);
        throw new Error(errorMessage);
      }
    },
  });
};

export const useRemoveDiscount = (bookingUuid: string) => {
  return useMutation({
    mutationFn: async (): Promise<BookingDetails> => {
      try {
        return await apiClient.delete<BookingDetails>(`/api/bookings/${bookingUuid}/discount`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        throw new Error(errorMessage);
      }
    },
  });
};
